import React, { forwardRef } from 'react';
import { TextField, TextFieldProps, IconButton, InputAdornment, Box } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { Add, Remove } from '@mui/icons-material';

interface NumericFormatFieldWithAdjustProps extends Omit<TextFieldProps, 'onChange' | 'defaultValue' | 'type' | 'InputProps'> {
  onValueChange?: (values: { value: string; floatValue: number | undefined }) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onAdjust?: (amount: number) => void;
  adjustAmount?: number;
  decimalScale?: number;
  thousandSeparator?: string;
  decimalSeparator?: string;
  allowNegative?: boolean;
  value?: string | number;
  defaultValue?: string | number;
  textAlign?: 'left' | 'center' | 'right';
  showAdjustButtons?: boolean;
  InputProps?: TextFieldProps['InputProps'];
}

const NumericFormatFieldWithAdjust = forwardRef<HTMLInputElement, NumericFormatFieldWithAdjustProps>(
  (
    {
      onValueChange,
      onChange,
      onAdjust,
      adjustAmount = 50000,
      decimalScale = 0,
      thousandSeparator = '.',
      decimalSeparator = ',',
      allowNegative = false,
      value,
      defaultValue,
      textAlign = 'right',
      showAdjustButtons = true,
      InputProps,
      ...textFieldProps
    },
    ref
  ) => {
    const handleAdjust = (amount: number, event: React.MouseEvent) => {
      // Get current value from props (value prop takes precedence over input field)
      // This ensures we use the controlled value from parent state
      const currentValueStr = value !== undefined && value !== null 
        ? String(value) 
        : (defaultValue !== undefined && defaultValue !== null 
          ? String(defaultValue) 
          : '0');
      
      // Parse the current value, removing formatting (thousand separators and decimal separator)
      const numericStr = currentValueStr.replace(/\./g, '').replace(',', '.');
      const currentNumericValue = parseFloat(numericStr) || 0;
      
      // Calculate new value
      const newValue = Math.max(0, currentNumericValue + amount);
      
      // Update via onValueChange (this will trigger parent state update)
      if (onValueChange) {
        onValueChange({ value: newValue.toString(), floatValue: newValue });
      }
      
      // Call onAdjust callback for additional logic if needed
      if (onAdjust) {
        onAdjust(amount);
      }
    };

    return (
      <NumericFormat
        customInput={TextField}
        getInputRef={ref}
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        onChange={onChange}
        decimalScale={decimalScale}
        thousandSeparator={thousandSeparator}
        decimalSeparator={decimalSeparator}
        allowNegative={allowNegative}
        InputProps={{
          ...InputProps,
          sx: {
            ...InputProps?.sx,
            ...(textAlign && {
              '& input': {
                textAlign: textAlign,
              },
            }),
          },
          endAdornment: showAdjustButtons ? (
            <InputAdornment position="end">
              <Box 
                className="numeric-format-adjust-buttons"
                sx={{ 
                  display: 'flex', 
                  gap: 0.5, 
                  alignItems: 'center', 
                  mr: -1,
                  '& .MuiIconButton-root': {
                    display: 'flex !important', // Override globalStyle rule
                  },
                }}
              >
                <IconButton
                  size="small"
                  onClick={(e) => handleAdjust(-adjustAmount, e)}
                  sx={{
                    padding: '4px',
                    display: 'flex !important', // Override globalStyle rule
                    '&:hover': {
                      backgroundColor: '#f3f4f6',
                    },
                  }}
                >
                  <Remove fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => handleAdjust(adjustAmount, e)}
                  sx={{
                    padding: '4px',
                    display: 'flex !important', // Override globalStyle rule
                    '&:hover': {
                      backgroundColor: '#f3f4f6',
                    },
                  }}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Box>
            </InputAdornment>
          ) : (
            InputProps?.endAdornment
          ),
        }}
        sx={{
          ...textFieldProps.sx,
        }}
        {...textFieldProps}
      />
    );
  }
);

NumericFormatFieldWithAdjust.displayName = 'NumericFormatFieldWithAdjust';

export default NumericFormatFieldWithAdjust;

